import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/config";
import { FaLock, FaUser, FaEye, FaEyeSlash } from "react-icons/fa";
import "./AdminLogin.css";
import { setPageMeta } from "../utils/seo";

const AdminRegister = () => {
  const [email, setEmail] = useState("admin@banatindia.com");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setPageMeta({
      title: "Admin Register | Banat India",
      description:
        "Create an administrator account for Banat India management tools.",
      keywords: "admin register, Banat India admin, management portal",
    });
  }, []);

  // Admin credentials (same as in AdminLogin)
  const ADMIN_EMAILS = [
    "admin@banatindia.com",
    "support@banatindia.com",
    "manager@banatindia.com"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      // Check if email is in admin list
      if (!ADMIN_EMAILS.includes(email.toLowerCase())) {
        setError("Access denied. Only authorized administrators can register.");
        setLoading(false);
        return;
      }

      // Create admin account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      if (userCredential.user) {
        setSuccess("Admin account created successfully! You can now log in.");
        setTimeout(() => {
          navigate("/admin-login");
        }, 2000);
      }
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setError("Admin account already exists. Please log in instead.");
      } else if (error.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <div className="admin-logo">
            <FaLock className="admin-icon" />
          </div>
          <h1>Create Admin Account</h1>
          <p>Register your administrator account</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && (
            <div className="error-message">
              <FaUser />
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <FaUser />
              {success}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Admin Email</label>
            <div className="input-wrapper">
              <FaUser className="input-icon" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@banatindia.com"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={loading}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                disabled={loading}
              />
            </div>
          </div>

          <button type="submit" className="admin-login-btn" disabled={loading}>
            {loading ? "Creating Account..." : "Create Admin Account"}
          </button>

          <div className="admin-auth-link">
            <button
              type="button"
              onClick={() => navigate("/admin-login")}
            >
              Already have an account? Log in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRegister; 